import { useEffect, useRef, useState } from 'react';
import { readDuration } from './tokens.ts';
import { useReducedMotion } from './useReducedMotion.ts';

export interface CountUpOptions {
  /** Duración en ms; por defecto `--duration-count`. */
  duration?: number;
  /** Valor desde el que arranca la primera animación. */
  from?: number;
}

/** Desaceleración equivalente a `--ease-enter`: llega rápido y se asienta. */
function easeOut(t: number): number {
  return 1 - (1 - t) ** 3;
}

/**
 * Valor que cuenta hasta `target` (y de un objetivo al siguiente si cambia).
 * Con movimiento reducido, o sin requestAnimationFrame, devuelve directamente el valor final.
 */
export function useCountUp(target: number, { duration, from = 0 }: CountUpOptions = {}): number {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(from);
  const current = useRef(from);
  const canFrame = typeof requestAnimationFrame === 'function';

  useEffect(() => {
    if (reduced || !canFrame) {
      current.current = target;
      return;
    }
    const start = current.current;
    const total = duration ?? readDuration('count');
    let frame = 0;
    let t0: number | undefined;
    const tick = (now: number) => {
      t0 ??= now;
      const progress = total <= 0 ? 1 : Math.min(1, (now - t0) / total);
      const next = progress >= 1 ? target : start + (target - start) * easeOut(progress);
      current.current = next;
      setValue(next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, reduced, canFrame]);

  return reduced || !canFrame ? target : value;
}
